import { db } from "./firebaseConfig";
import { doc, getDoc, setDoc, collection, getDocs, arrayUnion, updateDoc } from "firebase/firestore";


/**
 * Check if the user is allowed access to the job post automation page.
 * @param {string} email - The user's email address.
 * @returns {Promise<boolean>} - True if the email is in the allowed list, false otherwise.
 */
export const checkJobAutomationAccess = async (email) => {
  try {
    const docRef = doc(db, "Valarchi Admin", "Job Automate Access");
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const allowedEmails = data.ids || [];
      return allowedEmails.includes(email);
    } else {
      console.log("No such document!");
      return false;
    }
  } catch (error) {
    console.error("Error fetching document:", error);
    return false;
  }
};

/**
 * Fetches course details from Firestore based on the given course ID.
 * @param {string} courseId - The ID of the course (e.g., "VIT00001").
 * @returns {Promise<Object|null>} - Resolves to the course details object if found, otherwise `null`.
 * 
 * Input:
 *   courseId = "VIT00001"
 * 
 * Output:
 *   If course exists:
 *   {
 *     title: "Course Title",
 *     description: "Course Description",
 *     instructor: "Instructor Name",
 *     // Other course-specific fields
 *   }
 *   If course does not exist:
 *   null
 */
export const fetchCourseDetails = async (courseId) => {
  try {
    const courseRef = doc(db, "Valarchi Courses", courseId); // "Valarchi" is the collection name.
    const courseSnap = await getDoc(courseRef);

    if (courseSnap.exists()) {
      console.log("Course data:", courseSnap.data());
      return courseSnap.data();
    } else {
      console.log("No such course found!");
      return null;
    }
  } catch (error) {
    console.error("Error fetching course details:", error);
    throw error;
  }
};



/**
 * Saves or updates course details in Firestore.
 * @param {string} courseId - The ID of the course (e.g., "VIT00001").
 * @param {Object} courseData - The course data to save.
 * @returns {Promise<void>} - Resolves when the course is successfully saved.
 * 
 * Input:
 *   courseId = "VIT00001"
 *   courseData = {
 *     title: "Updated Course Title",
 *     description: "Updated Course Description",
 *     instructor: "Updated Instructor Name",
 *     // Other course-specific fields
 *   }
 * 
 * Output:
 *   Logs "Course saved successfully!" on success.
 */
export const saveCourseDetails = async (courseId, courseData) => {
  try {
    const courseRef = doc(db, "Valarchi Courses", courseId);
    await setDoc(courseRef, courseData, { merge: true });
    console.log("Course saved successfully!");
  } catch (error) {
    console.error("Error saving course details:", error);
    throw error;
  }
};

/**
 * Add a user email to the apply_url_clicked array in a course document.
 * @param {string} courseId
 * @param {string} userEmail
 * @returns {Promise<void>}
 */
export const addUserToApplyClicked = async (courseId, userEmail) => {
  try {
    const courseRef = doc(db, "Valarchi Courses", courseId);
    await updateDoc(courseRef, {
      apply_url_clicked: arrayUnion(userEmail), // Ensures no duplicates
    });
    console.log(`User email (${userEmail}) added to apply_url_clicked!`);
  } catch (error) {
    console.error("Error adding user email to apply_url_clicked:", error);
    throw error;
  }
};


/**
 * Get the next course ID in the format VIT00001, VIT00002, etc.
 * @returns {Promise<string>} - The next course ID.
 */
export const getNextCourseId = async () => {
  try {
    const valarchiCollection = collection(db, "Valarchi Courses");
    const snapshot = await getDocs(valarchiCollection);
    let documentCount = snapshot.size; // Number of documents in the collection

    // If the collection is empty or not found, set the count to 0
    if (documentCount === 0) {
      documentCount = 0;
    }

    // Generate the next course ID with leading zeroes
    const nextCourseId = `itv${String(documentCount + 1)}`;
    return nextCourseId;
  } catch (error) {
    console.error("Error getting the next course ID:", error);
    throw error;
  }
};





/**
 * Fetches the list of all courses in the Firestore "Valarchi" collection.
 * @returns {Promise<Array<Object>>} - Resolves to an array of course objects.
 * 
 * Input:
 *   None
 * 
 * Output:
 *   [
 *     {
 *       id: "VIT00001",
 *       title: "Course Title 1",
 *       description: "Course Description 1",
 *       instructor: "Instructor Name 1",
 *       // Other course-specific fields
 *     },
 *     {
 *       id: "VIT00002",
 *       title: "Course Title 2",
 *       description: "Course Description 2",
 *       instructor: "Instructor Name 2",
 *       // Other course-specific fields
 *     },
 *     // Additional courses
 *   ]
 */
export const fetchAllCourses = async () => {
  try {
    const coursesRef = collection(db, "Valarchi Courses");
    const coursesSnap = await getDocs(coursesRef);

    const courses = coursesSnap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }));

    console.log("Fetched all courses:", courses);
    return courses;
  } catch (error) {
    console.error("Error fetching courses:", error);
    throw error;
  }
};




/**
 * Saves user enrollment information in Firestore.
 * @param {string} userEmail - The email of the user (e.g., "user@example.com").
 * @param {Object} enrollmentData - The enrollment data to save.
 * @returns {Promise<void>} - Resolves when the enrollment data is successfully saved.
 * 
 * Input:
 *   userEmail = "user@example.com"
 *   enrollmentData = {
 *     courseId: "VIT00001",
 *     enrolledDate: "2025-01-10",
 *     progress: 0, // Percentage
 *     // Other enrollment-specific fields
 *   }
 * 
 * Output:
 *   Logs "User enrollment updated successfully!" on success.
 */
export const updateUserEnrollment = async (userEmail, enrollmentData) => {
  try {
    const userRef = doc(db, "Valarchi Users", userEmail);

    // Fetch the current user's document to check for existing enrolled courses
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      // If the user document exists, get the current enrolled courses
      const userData = userDoc.data();
      const enrolledCourses = userData.enrolled || {};

      // Add/Update the course enrollment details using the courseId as the key
      enrolledCourses[enrollmentData.id] = enrollmentData;

      // Update the user document with the new enrollment data
      await setDoc(userRef, { enrolled: enrolledCourses }, { merge: true });
      console.log("User enrollment updated successfully!");
    } else {
      // If the user document does not exist, create a new one with the enrollment data
      const newEnrollment = {
        enrolled: {
          [enrollmentData.id]: enrollmentData,
        },
      };

      await setDoc(userRef, newEnrollment);
      console.log("User document created and enrollment added successfully!");
    }
  } catch (error) {
    console.error("Error updating user enrollment:", error);
    throw error;
  }
};



export const fetchUserData = async (user_mail) => {
  try {
    const courseRef = doc(db, "Valarchi Users", user_mail); // "Valarchi" is the collection name.
    const courseSnap = await getDoc(courseRef);

    if (courseSnap.exists()) {
      console.log("User data:", courseSnap.data());
      return courseSnap.data();
    } else {
      console.log("No such user found!");
      return null;
    }
  } catch (error) {
    console.error("Error fetching user details:", error);
    throw error;
  }
};